import {useState, useEffect, useContext, useRef} from 'react';
import {MainContext} from '../../../../contexts/MainContext';
import {MapContext} from '../../../../contexts/MapContext';
import {useFindPath} from '../../../../hooks/find-path';
import './MarkersPath.scss';

export default function MarkersPath({mapName, naturalSize}) {
    const {zoomStyles, setMarkersPathStatus, setRouteDataExtended} = useContext(MapContext);
    const {showDistances, communitiesMarkers} = useContext(MainContext);
    const [selectedMarkers, setSelectedMarkers] = useState([]);
    const [showText, setShowText] = useState(false);
    const polylineRef = useRef(null);

    // use the custom hook only when exactly two markers are selected.
    const routeData = useFindPath(
        selectedMarkers.length === 2 ? selectedMarkers[0].coords : null,
        selectedMarkers.length === 2 ? selectedMarkers[1].coords : null,
        mapName
    );

    useEffect(() => {
        if (routeData && selectedMarkers.length === 2) {
            let routeDataExtended = {
                response: routeData,
                startName: selectedMarkers[0].name,
                endName: selectedMarkers[1].name,
            };
            setRouteDataExtended(routeDataExtended);
        } else {
            setRouteDataExtended(null);
        }
    }, [routeData, setRouteDataExtended, selectedMarkers]);

    // Clear selected markers and path when switching between communities and properties views
    useEffect(() => {
        setSelectedMarkers([]);
        setShowText(false);
    }, [communitiesMarkers]);

    const handleMarkerSelection = (marker) => {
        // if two markers are already selected (i.e. road is shown), restart selection with the clicked marker.
        if (selectedMarkers.length === 2) {
            setSelectedMarkers([marker]);
            setShowText(false);
            return;
        }
        // if the marker is already selected (and only one marker is selected), unselect it.
        if (selectedMarkers.some((m) => m.name === marker.name)) {
            setSelectedMarkers(selectedMarkers.filter((m) => m.name !== marker.name));
            setShowText(false);
            return;
        }
        // otherwise, add the marker.
        setSelectedMarkers((prev) => [...prev, marker]);
    };

    // whenever showDistances is turned off, clear any marker selections.
    useEffect(() => {
        if (!showDistances) {
            setSelectedMarkers([]);
            setShowText(false);
        }
    }, [showDistances]);

    // hide the text if the selection changes (e.g. if not exactly 2 markers are selected).
    useEffect(() => {
        if (selectedMarkers.length !== 2) {
            setShowText(false);
        }
    }, [selectedMarkers]);

    // listen for the custom "mapImageClicked" event to clear marker selections.
    useEffect(() => {
        const handleMapImageClicked = () => {
            setSelectedMarkers([]);
            setShowText(false);
        };

        window.addEventListener("mapImageClicked", handleMapImageClicked);
        return () => {
            window.removeEventListener("mapImageClicked", handleMapImageClicked);
        };
    }, []);

    // update marker styles based on the current selection.
    useEffect(() => {
        const markers = document.querySelectorAll('.marker-group, .marker-group-fac');
        markers.forEach((marker) => {
            marker.classList.remove('selected-marker', 'dim-marker-first', 'dim-marker-second');
            const markerName = marker.getAttribute('data-name');
            if (selectedMarkers.length === 1) {
                if (selectedMarkers[0].name === markerName) {
                    marker.classList.add('selected-marker');
                } else {
                    marker.classList.add('dim-marker-first');
                }
            } else if (selectedMarkers.length === 2) {
                if (selectedMarkers.some((m) => m.name === markerName)) {
                    marker.classList.add('selected-marker');
                } else {
                    marker.classList.add('dim-marker-second');
                }
            }
            // No classes are applied if no selection exists.
        });
    }, [selectedMarkers]);

    // attach an event listener on all SVG elements inside the map
    useEffect(() => {
        if (!showDistances) return;

        const handleClick = (e) => {
            const markerGroup = e.target.closest('.marker-group, .marker-group-fac');
            if (markerGroup) {
                const coordsAttr = markerGroup.getAttribute('data-coords');
                const name = markerGroup.getAttribute('data-name');
                if (coordsAttr && name) {
                    const coords = coordsAttr.split(',').map(Number);
                    e.stopPropagation();
                    e.preventDefault();
                    handleMarkerSelection({coords, name});
                }
            }
        };

        const svgElements = document.querySelectorAll('.map svg');
        svgElements.forEach((svg) => {
            svg.addEventListener('click', handleClick, true);
        });

        return () => {
            svgElements.forEach((svg) => {
                svg.removeEventListener('click', handleClick, true);
            });
        };
    }, [showDistances, selectedMarkers]);

    // animate the polyline drawing when the route data is available.
    useEffect(() => {
        if (!routeData || !routeData.path || routeData.path.length === 0) return;
        if (polylineRef.current) {
            const length = polylineRef.current.getTotalLength();
            // Set up for the drawing animation.
            polylineRef.current.style.strokeDasharray = length;
            polylineRef.current.style.strokeDashoffset = length;
            // Force a reflow to ensure the styles are applied.
            polylineRef.current.getBoundingClientRect();
            // Animate the dash offset to zero.
            polylineRef.current.style.transition = 'stroke-dashoffset 2s ease-in-out';
            polylineRef.current.style.strokeDashoffset = '0';
            // Once the animation completes, set the dash pattern for a dashed look and trigger text fade-in.
            const handleTransitionEnd = () => {
                if (polylineRef.current) {
                    polylineRef.current.style.strokeDasharray = '20,20';
                }
                // Delay the text appearance slightly for a smooth fade-in.
                setTimeout(() => {
                    setShowText(true);
                }, 300);
            };
            polylineRef.current.addEventListener('transitionend', handleTransitionEnd, {once: true});
        }
    }, [routeData]);

    // updating markersPathStatus based on current state.
    useEffect(() => {
        if (!showDistances) {
            setMarkersPathStatus("idle");
        } else if (selectedMarkers.length !== 2) {
            setMarkersPathStatus("instruction");
        } else {
            if (!routeData || !routeData.path || routeData.path.length === 0) {
                setMarkersPathStatus("loading");
            } else {
                setMarkersPathStatus("displayed");
            }
        }
    }, [showDistances, selectedMarkers, routeData, setMarkersPathStatus]);

    // do not render the path unless exactly two markers are selected.
    if (
        !showDistances ||
        selectedMarkers.length !== 2 ||
        !routeData ||
        !routeData.path ||
        routeData.path.length === 0
    ) {
        return null;
    }

    const points = routeData.path.map((pt) => pt.join(',')).join(' ');
    const midIndex = Math.floor(routeData.path.length / 2);
    const midPoint = routeData.path[midIndex];

    return (
        <svg
            className="path-between-markers-overlay"
            viewBox={`0 0 ${naturalSize.width} ${naturalSize.height}`}
            style={zoomStyles}
        >
            <defs>
                <filter id="dropShadow">
                    <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000000" floodOpacity="0.55"/>
                </filter>
            </defs>
            <polyline
                ref={polylineRef}
                points={points}
                stroke="#9ED9E3"
                strokeWidth="12"
                fill="none"
                filter="url(#dropShadow)"
            />
            {showText && (
                <text
                    x={midPoint[0]}
                    y={midPoint[1] - 50}
                    fill="white"
                    fontSize="50"
                    textAnchor="middle"
                    fontFamily="Acumin Pro Medium"
                    style={{opacity: showText ? 1 : 0, transition: 'opacity 1s ease-in-out'}}
                >
                    {`${routeData.travel_time_min.toFixed(1)} min | ${routeData.distance_km.toFixed(1)} km`}
                </text>
            )}
        </svg>
    );
}
